import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getSession } from './useSession.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { toast } from 'sonner';
import { usePartnerDetails } from './usePartnerDetails.ts';

export interface AddDomainRequest {
  domain: string;
  projectId?: string;
}

export interface AddDomainResponse {
  message: string;
  description: string;
  data: Array<{
    name: string;
    type: string;
    value: string | string[];
  }>;
  error?: string;
}

// Function to add domain to Vercel
async function addDomainToVercel(domain: string) {
  const session = await getSession();

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/add-domain/connect`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ domain: domain.trim() }),
    }
  );
  
  const data = await response.json();  
  if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`);
  return data;
}
// Function to delete domain from Vercel (async function, not hook)
export async function deleteDomainFromVercel(domain: string) {
  const session = await getSession();

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/add-domain/remove`,
    {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ domain: domain.trim() }),
    }
  );
  
  const data = await response.json();  
  if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`);
  return data;
}

// Hook to delete domain using React Query
export function useDeleteDomain() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (domain: string) => {
      await deleteDomainFromVercel(domain.trim());
      queryClient.invalidateQueries({ queryKey: ['user-domain'] });
      
      return domain;
    },
    onSuccess: (domain) => {
      toast.success(`Domain ${domain} removed successfully`);
      queryClient.invalidateQueries({ queryKey: ['user-domain'] });
    },
    onError: (error: any, domain) => {
      console.error('Failed to delete domain:', error);
      toast.error(`Failed to delete domain ${domain}: ${error.message}`);
    },
  });
}

// Hook to add domain using TanStack Query (only for white-label partners)
export function useAddDomain() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { data: partnerDetails } = usePartnerDetails();

  return useMutation({
    mutationFn: async (domain: string) => {
      if (!user) throw new Error('Not authenticated');

      // Check if user is white-label partner
      if (partnerDetails?.partner_type !== 'white_label') {
        throw new Error('Only white-label partners can add custom domains');
      }

      // Step 1: Add domain to Vercel
      const vercelResult = await addDomainToVercel(domain.trim());
      if (vercelResult?.error) throw new Error(vercelResult?.error);
      return domain;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['user-domain']}),
    onError: (error, domain) => {
      console.error(`Failed to add ${domain} domain:`, error);
      toast.error(`Failed to add ${domain} domain`);
    },
  });
}